import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AssetRequestReviewOutcome, AssetRequestStatus } from '@cfl/assets-shared';

import { api } from '@/lib/api';

export interface AssetRequest {
  id: string;
  category: { id: string; name: string };
  requestedBy: { id: string; name: string; employeeCode: string };
  forEmployee: {
    id: string;
    name: string;
    employeeCode: string;
    department: string | null;
    status: string;
  };
  justification: string;
  /** The date it is wanted by, or null for "when you get a chance". */
  neededBy: string | null;
  status: AssetRequestStatus;
  reviewNote: string | null;
  reviewedByName: string | null;
  reviewedAt: string | null;
  /** What was actually issued against it. Only ever set on a FULFILLED request. */
  fulfilledWith: {
    assignmentId: string;
    assignedAt: string | null;
    asset: { id: string; code: string; name: string };
  } | null;
  createdAt: string;
}

export const assetRequestKeys = {
  all: ['asset-requests'] as const,
  mine: ['asset-requests', 'mine'] as const,
  queue: (status?: string) => ['asset-requests', 'queue', status ?? 'all'] as const,
  forEmployee: (employeeId: string) => ['asset-requests', 'employee', employeeId] as const,
};

/**
 * What I asked for, and what was asked for on my behalf.
 *
 * Both, because a joiner whose manager requested their laptop would otherwise see an
 * empty screen while a request for them sits in IT's queue.
 */
export function useMyAssetRequests() {
  return useQuery({
    queryKey: assetRequestKeys.mine,
    queryFn: () => api.get<AssetRequest[]>('/asset-requests/mine'),
  });
}

export function useAssetRequestQueue(
  status?: AssetRequestStatus | '',
  options: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: assetRequestKeys.queue(status || undefined),
    queryFn: () => api.get<AssetRequest[]>(`/asset-requests${status ? `?status=${status}` : ''}`),
    enabled: options.enabled ?? true,
  });
}

/** Open requests for one person, for their employee screen. */
export function useEmployeeAssetRequests(employeeId: string | null) {
  return useQuery({
    queryKey: assetRequestKeys.forEmployee(employeeId ?? ''),
    queryFn: () => api.get<AssetRequest[]>(`/asset-requests/employee/${employeeId}`),
    enabled: employeeId !== null && employeeId !== '',
  });
}

export function useRaiseAssetRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      categoryId: string;
      forEmployeeId?: string;
      justification: string;
      neededBy?: string | null;
    }) => api.post<AssetRequest>('/asset-requests', input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: assetRequestKeys.all }),
  });
}

export function useReviewAssetRequest(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { status: AssetRequestReviewOutcome; note?: string | null }) =>
      api.patch<AssetRequest>(`/asset-requests/${id}/review`, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: assetRequestKeys.all }),
  });
}

export function useCancelAssetRequest(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.patch<AssetRequest>(`/asset-requests/${id}/cancel`, {}),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: assetRequestKeys.all }),
  });
}

/**
 * There is deliberately no `useFulfilAssetRequest`.
 *
 * A request becomes FULFILLED only by issuing something against it on the Assign screen,
 * which passes `fulfilsRequestId` to useAssignAsset - so the status can never exist
 * without a real handover behind it, and the database refuses the pairing otherwise. A
 * "mark fulfilled" mutation here would be the single way to break that guarantee, so it
 * does not exist; useAssignAsset invalidates `assetRequestKeys.all` instead.
 */
